import * as React from 'react';
import { isEmpty } from 'lodash';
import { Formik, Form } from 'formik';
import { NavLink, Redirect, useLocation } from 'react-router-dom';
import AuthContext from './AuthContext';

import Loader from './Loader';
import NotifyHOC from './HOC/Notifications';
import { User } from '../types/User';
import { resetPasswordValidationSchema } from '../utils/validation';
import Logo from '../assets/SVG/nexxt-logo';
import TextField from './TextField';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import { LOGIN } from '../constants/routes';

interface IResetPasswordProps {
    user: User;
    location: any;
    toastNotification: any;
    requestingSignup: boolean;
}
interface IResetPasswordState {
    email: string;
    password: string;
    isUnvalid: boolean;
    isRequesting: boolean;
    successfullyReset: boolean;
}

const CODE_INVALID = `Your validation actionCode is incorrect. Please contact ${process.env.CONTACT_EMAIL_ADDRESS}!`;
const SUCCESS_UPDATE_PASSWORD = 'Successfully updated password.';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const ResetPassword = (props: IResetPasswordProps) => {
    const [invalidUrl, setInvalidUrl] = React.useState(false);
    const [isRequesting, setIsRequesting] = React.useState(false);
    const [successfullyReset, setSuccessfullyReset] = React.useState(false);
    const { firebaseAuth } = React.useContext(AuthContext);

    const urlQuery = useQuery();
    const actionCode = urlQuery.get('oobCode');

    React.useEffect(() => {
        async function validateUrl() {
            try {
                const email = await firebaseAuth.verifyPasswordResetCode(
                    actionCode
                );

                if (!email) {
                    setInvalidUrl(true);
                }
            } catch (error) {
                setInvalidUrl(true);
            }
        }

        validateUrl();
    }, []);

    React.useEffect(() => {
        if (invalidUrl) {
            props.toastNotification.error(CODE_INVALID);
        }
    }, [invalidUrl]);

    const handleSubmit = async (values: any, { setSubmitting }) => {
        resetPassword(values, setSubmitting);
    };

    const resetPassword = async (values, setSubmitting) => {
        const { password: newPassword } = values;

        setIsRequesting(true);
        try {
            if (!invalidUrl) {
                await firebaseAuth.confirmPasswordReset(
                    actionCode,
                    newPassword
                );

                setSuccessfullyReset(true);
                setIsRequesting(false);
                setSubmitting(false);
                props.toastNotification.success(SUCCESS_UPDATE_PASSWORD);
            }
        } catch (error) {
            setSubmitting(false);
            setIsRequesting(false);
            props.toastNotification.error(error.response.data.error.message);
        }
    };

    if (successfullyReset) {
        return <Redirect to={LOGIN} />;
    }

    return (
        <div className="signup--container">
            {isRequesting ? <Loader isFixed={true} /> : null}

            <div className="auth--container">
                <div className="auth--wrapper">
                    <div className="auth--header">
                        <NavLink exact={true} to={LOGIN}>
                            <Logo />
                        </NavLink>
                        <p className="auth--header--title">
                            Reset your password.
                        </p>
                        <p className="auth--header--subtitle">
                            Your password must be at least eight characters.
                        </p>
                    </div>
                    <div className="auth--body">
                        <Formik
                            initialValues={{
                                password: '',
                                confirmPassword: ''
                            }}
                            validationSchema={resetPasswordValidationSchema}
                            onSubmit={(values, actions) => {
                                handleSubmit(values, actions);
                            }}
                            render={({
                                values,
                                touched,
                                handleChange,
                                handleBlur,
                                errors,
                                dirty
                            }) => (
                                <Form className="registration-forms">
                                    <TextField
                                        type="password"
                                        name="password"
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Password"
                                        label="Password"
                                        errorMessage={errors.password}
                                        touchedValue={touched.password}
                                        disabled={invalidUrl}
                                    />
                                    <TextField
                                        type="password"
                                        name="confirmPassword"
                                        value={values.confirmPassword}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Confirm Password"
                                        label="Confirm Password"
                                        errorMessage={errors.confirmPassword}
                                        touchedValue={touched.confirmPassword}
                                        disabled={invalidUrl}
                                    />
                                    <div className="form--footer">
                                        <button
                                            disabled={
                                                !isEmpty(errors) ||
                                                isRequesting ||
                                                !dirty ||
                                                invalidUrl
                                            }
                                            type="submit"
                                            className="form-submit__button small"
                                        >
                                            Reset password
                                        </button>
                                    </div>
                                </Form>
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className="page--byline">
                <a
                    className="by-logo"
                    href="https://nexxt.in"
                    target="_blank"
                    rel="noreferrer"
                >
                    <img
                        src={`${STATIC_STORAGE_URL}/img/powered_by_nexxt_intelligence_inca.png`}
                    />
                </a>
            </div>
        </div>
    );
};

export default NotifyHOC(ResetPassword);
